// lib/worldDestinations.js and components/explore/WorldDestinations.jsx: the
// world destinations Toukky suggests (Home's "Explore the world", the flights
// page). City names by airport code; country names come from
// Intl.DisplayNames in the visitor's language. Nothing here is a price, a
// count or a claim: a fare or a number of stays is shown only when real data
// says so (staysLabel, with no number, when the count is not known in full).
// Every card searches the form's default trip (leaving tomorrow for a week),
// and `sub` says so.
import { plural } from "@/lib/format";

const counted = (lang, forms) => (n) => plural(n, forms, { lang });

export const worldL = {
  en: {
    heading: "Explore the world",
    sub: "Ideas on every continent. Each card searches round-trip flights to that city, leaving tomorrow for a week; you can change the dates in the results.",
    flights: "Flights",
    flightsTo: (city) => `Flights to ${city}`,
    flightsFrom: (price) => `Flights from ${price}`,
    staysLabel: "Stays",
    stays: counted("en", { one: "# stay", other: "# stays" }),
    staysAt: (count, city) => `${count} in ${city}`,
    faresNote: (city) => `Round-trip flights from ${city}, one adult in economy, on the dates shown. Prices are indicative and checked again when you search.`,
    cities: {
      CDG: "Paris", DXB: "Dubai", JFK: "New York", NRT: "Tokyo", IST: "Istanbul", LHR: "London",
      RAK: "Marrakech", CPT: "Cape Town", BKK: "Bangkok", FCO: "Rome", GIG: "Rio de Janeiro", DSS: "Dakar",
    },
  },
  fr: {
    heading: "Explorez le monde",
    sub: "Des idées sur tous les continents. Chaque carte cherche des vols aller-retour vers cette ville, départ demain pour une semaine ; dates modifiables dans les résultats.",
    flights: "Vols",
    flightsTo: (city) => `Vols vers ${city}`,
    flightsFrom: (price) => `Vols dès ${price}`,
    staysLabel: "Séjours",
    stays: counted("fr", { one: "# séjour", other: "# séjours" }),
    staysAt: (count, city) => `${count} à ${city}`,
    faresNote: (city) => `Vols aller-retour depuis ${city}, un adulte en économie, aux dates indiquées. Prix indicatifs, revérifiés lors de la recherche.`,
    cities: {
      CDG: "Paris", DXB: "Dubaï", JFK: "New York", NRT: "Tokyo", IST: "Istanbul", LHR: "Londres",
      RAK: "Marrakech", CPT: "Le Cap", BKK: "Bangkok", FCO: "Rome", GIG: "Rio de Janeiro", DSS: "Dakar",
    },
  },
  es: {
    heading: "Explore el mundo",
    sub: "Ideas en todos los continentes. Cada tarjeta busca vuelos de ida y vuelta a esa ciudad, con salida mañana durante una semana; puede cambiar las fechas en los resultados.",
    flights: "Vuelos",
    flightsTo: (city) => `Vuelos a ${city}`,
    flightsFrom: (price) => `Vuelos desde ${price}`,
    staysLabel: "Alojamientos",
    stays: counted("es", { one: "# alojamiento", other: "# alojamientos" }),
    staysAt: (count, city) => `${count} en ${city}`,
    faresNote: (city) => `Vuelos de ida y vuelta desde ${city}, un adulto en turista, en las fechas indicadas. Precios orientativos, se verifican al buscar.`,
    cities: {
      CDG: "París", DXB: "Dubái", JFK: "Nueva York", NRT: "Tokio", IST: "Estambul", LHR: "Londres",
      RAK: "Marrakech", CPT: "Ciudad del Cabo", BKK: "Bangkok", FCO: "Roma", GIG: "Río de Janeiro", DSS: "Dakar",
    },
  },
  pt: {
    heading: "Explore o mundo",
    sub: "Ideias em todos os continentes. Cada cartão pesquisa voos de ida e volta para essa cidade, com partida amanhã por uma semana; pode alterar as datas nos resultados.",
    flights: "Voos",
    flightsTo: (city) => `Voos para ${city}`,
    flightsFrom: (price) => `Voos desde ${price}`,
    staysLabel: "Alojamentos",
    stays: counted("pt", { one: "# alojamento", other: "# alojamentos" }),
    staysAt: (count, city) => `${count} em ${city}`,
    faresNote: (city) => `Voos de ida e volta desde ${city}, um adulto em económica, nas datas indicadas. Preços indicativos, verificados na pesquisa.`,
    cities: {
      CDG: "Paris", DXB: "Dubai", JFK: "Nova Iorque", NRT: "Tóquio", IST: "Istambul", LHR: "Londres",
      RAK: "Marraquexe", CPT: "Cidade do Cabo", BKK: "Banguecoque", FCO: "Roma", GIG: "Rio de Janeiro", DSS: "Dacar",
    },
  },
  ar: {
    heading: "استكشف العالم",
    sub: "أفكار في كل القارات. تبحث كل بطاقة عن رحلات ذهاب وعودة إلى تلك المدينة، بمغادرة غدًا لمدة أسبوع، ويمكنك تغيير التواريخ في النتائج.",
    flights: "رحلات",
    flightsTo: (city) => `رحلات إلى ${city}`,
    flightsFrom: (price) => `رحلات من ${price}`,
    staysLabel: "أماكن إقامة",
    stays: counted("ar", { zero: "# مكان إقامة", one: "مكان إقامة واحد", two: "مكانا إقامة", few: "# أماكن إقامة", many: "# مكان إقامة", other: "# مكان إقامة" }),
    staysAt: (count, city) => `${count} في ${city}`,
    faresNote: (city) => `رحلات ذهاب وعودة من ${city}، لبالغ واحد في الدرجة الاقتصادية، في التواريخ المعروضة. الأسعار إرشادية ويُعاد التحقق منها عند البحث.`,
    cities: {
      CDG: "باريس", DXB: "دبي", JFK: "نيويورك", NRT: "طوكيو", IST: "إسطنبول", LHR: "لندن",
      RAK: "مراكش", CPT: "كيب تاون", BKK: "بانكوك", FCO: "روما", GIG: "ريو دي جانيرو", DSS: "داكار",
    },
  },
  zh: {
    heading: "探索世界",
    sub: "遍及各大洲的目的地灵感。点击卡片即可搜索飞往该城市的往返航班，明天出发、一周后返回；可在搜索结果中修改日期。",
    flights: "航班",
    flightsTo: (city) => `飞往${city}的航班`,
    flightsFrom: (price) => `机票 ${price} 起`,
    staysLabel: "住宿",
    stays: counted("zh", { other: "# 处住宿" }),
    staysAt: (count, city) => `${city}：${count}`,
    faresNote: (city) => `自${city}出发的往返机票，1 位成人经济舱，按所示日期。价格仅供参考，搜索时会重新核实。`,
    cities: {
      CDG: "巴黎", DXB: "迪拜", JFK: "纽约", NRT: "东京", IST: "伊斯坦布尔", LHR: "伦敦",
      RAK: "马拉喀什", CPT: "开普敦", BKK: "曼谷", FCO: "罗马", GIG: "里约热内卢", DSS: "达喀尔",
    },
  },
};
